import { BackSide, Mesh, ShaderMaterial, SphereGeometry } from "three";
import type { AtmosphereUniforms } from "../atmosphere.ts";
import { assemble } from "./chunks.ts";

const vertexShader = /* glsl */ `
out vec3 vDir;
void main() {
  vDir = normalize(position);
  vec4 world = modelMatrix * vec4(position, 1.0);
  gl_Position = projectionMatrix * viewMatrix * world;
  gl_Position.z = gl_Position.w; // sempre no fundo
}
`;

const fragmentShader = /* glsl */ `
${assemble("fragment", ["atmo", "hash", "fogGlow"])}
in vec3 vDir;

void main() {
  vec3 dir = normalize(vDir);
  float elevation = dir.y;
  // Uniforms de THREE.Color já estão em espaço linear.
  vec3 zenith = uZenith;
  vec3 horizon = uHorizon;
  vec3 color = mix(horizon, zenith, pow(clamp(elevation, 0.0, 1.0), 0.55));
  // Faixa do horizonte na cor da neblina do terreno (com o brilho do sol, F51): o mar distante se
  // funde ao céu.
  color = mix(color, fogTint(dir), 1.0 - smoothstep(-0.02, 0.2, elevation));

  float sunDot = dot(dir, normalize(uSunDir));
  float sunVisible = 1.0 - uNight;
  // Sol, a coroa e um brilho largo de névoa em volta (Fase 13: o céu "com névoa e bruma").
  color += uLightColor * (smoothstep(0.99985, 0.99996, sunDot) * 2.5 + pow(max(sunDot, 0.0), 64.0) * 0.6 + pow(max(sunDot, 0.0), 6.0) * 0.18 * (1.0 - smoothstep(0.0, 0.5, elevation))) * sunVisible;

  // Lua (oposta ao sol) e estrelas à noite.
  float moonDot = dot(dir, -normalize(uSunDir));
  color += vec3(0.75, 0.82, 1.0) * smoothstep(0.9990, 0.9995, moonDot) * uNight * 2.0;
  // Estrelas: uma por célula sorteada, como ponto redondo e pequeno num lugar aleatório da célula
  // (a célula inteira acesa virava um quadrado na tela). A largura em pixels vem da derivada.
  vec2 grid = dir.xz / (dir.y + 1.2) * 180.0;
  vec2 cell = floor(grid);
  vec2 center = vec2(hash12(cell + 3.1), hash12(cell + 5.7)) * 0.6 + 0.2;
  float px = max(fwidth(grid.x), 1e-4);
  float dotMask = 1.0 - smoothstep(0.6 * px, 1.6 * px, length(fract(grid) - center));
  float star = step(0.9975, hash12(cell)) * dotMask * smoothstep(0.0, 0.25, elevation);
  float twinkle = 0.6 + 0.4 * sin(uTime * 2.0 + hash12(cell + 7.0) * 40.0);
  color += vec3(star * twinkle * smoothstep(0.5, 1.0, uNight));
  // Pesquisa 11: o céu sai na paleta coordenada (taupe no zênite, creme no horizonte), sem a
  // dessaturação da F59 — a faixa quente do horizonte é a âncora das referências.

  gl_FragColor = vec4(color, 1.0);
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}
`;

/** Abóbada celeste: esfera de baixa resolução que acompanha a câmera, sem escrever profundidade. */
export function createSky(atmosphere: AtmosphereUniforms): Mesh {
  const material = new ShaderMaterial({
    name: "ceu/abobada",
    uniforms: { ...atmosphere },
    vertexShader,
    fragmentShader,
    side: BackSide,
    depthWrite: false,
  });
  const sky = new Mesh(new SphereGeometry(1, 32, 16), material);
  sky.scale.setScalar(3000);
  sky.renderOrder = -10;
  sky.frustumCulled = false;
  return sky;
}
